import { AlertTriangle, Clock, TrendingUp, Wrench } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

interface AssetPerformanceTabProps {
  templateId: string;
}

const AssetPerformanceTab: React.FC<AssetPerformanceTabProps> = ({ templateId }) => {
  const [stats, setStats] = useState({
    totalInstances: 0,
    availableInstances: 0,
    rentedInstances: 0,
    maintenanceInstances: 0,
    retiredInstances: 0,
    missingInstances: 0,
    utilizationRate: 0,
    averageConditionScore: 0,
    totalMaintenanceCost: 0,
    totalAcquisitionCost: 0,
    maintenanceFrequency: 0,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPerformanceStats();
  }, [templateId]);

  const loadPerformanceStats = async () => {
    try {
      setLoading(true);

      // For now, we'll use mock data since the analytics endpoint isn't implemented yet
      // TODO: Replace with actual API call to /api/asset-templates/{templateId}/performance

      setTimeout(() => {
        setStats({
          totalInstances: 25,
          availableInstances: 18,
          rentedInstances: 5,
          maintenanceInstances: 2,
          retiredInstances: 0,
          missingInstances: 0,
          utilizationRate: 72,
          averageConditionScore: 4.2,
          totalMaintenanceCost: 1850,
          totalAcquisitionCost: 12500,
          maintenanceFrequency: 0.8,
        });
        setLoading(false);
      }, 500);
    } catch (err) {
      console.error('Error loading performance stats:', err);
      setLoading(false);
    }
  };

  const getUtilizationBadge = (rate: number) => {
    if (rate >= 80) return { variant: 'default' as const, label: 'Excellent' };
    if (rate >= 60) return { variant: 'secondary' as const, label: 'Good' };
    if (rate >= 40) return { variant: 'warning' as const, label: 'Fair' };
    return { variant: 'destructive' as const, label: 'Poor' };
  };

  const getConditionBadge = (score: number) => {
    if (score >= 4.5) return { variant: 'default' as const, label: 'Excellent' };
    if (score >= 3.5) return { variant: 'secondary' as const, label: 'Good' };
    if (score >= 2.5) return { variant: 'warning' as const, label: 'Fair' };
    return { variant: 'destructive' as const, label: 'Poor' };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">Loading performance data...</div>
      </div>
    );
  }

  const utilizationBadge = getUtilizationBadge(stats.utilizationRate);
  const conditionBadge = getConditionBadge(stats.averageConditionScore);

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utilization Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.utilizationRate}%</div>
            <div className="mt-2">
              <Badge variant={utilizationBadge.variant}>{utilizationBadge.label}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Condition</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.averageConditionScore}/5</div>
            <div className="mt-2">
              <Badge variant={conditionBadge.variant}>{conditionBadge.label}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Maintenance Cost</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{stats.totalMaintenanceCost.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {((stats.totalMaintenanceCost / stats.totalAcquisitionCost) * 100).toFixed(1)}% of acquisition cost
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Maintenance Frequency</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.maintenanceFrequency}</div>
            <p className="text-xs text-muted-foreground">per instance per year</p>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Unit Status Distribution</CardTitle>
          <CardDescription>Current status of all instances</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.availableInstances}</div>
              <div className="text-sm text-gray-600">Available</div>
              <div className="text-xs text-gray-500">
                {((stats.availableInstances / stats.totalInstances) * 100).toFixed(0)}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.rentedInstances}</div>
              <div className="text-sm text-gray-600">Rented</div>
              <div className="text-xs text-gray-500">
                {((stats.rentedInstances / stats.totalInstances) * 100).toFixed(0)}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.maintenanceInstances}</div>
              <div className="text-sm text-gray-600">Maintenance</div>
              <div className="text-xs text-gray-500">
                {((stats.maintenanceInstances / stats.totalInstances) * 100).toFixed(0)}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{stats.retiredInstances}</div>
              <div className="text-sm text-gray-600">Retired</div>
              <div className="text-xs text-gray-500">
                {((stats.retiredInstances / stats.totalInstances) * 100).toFixed(0)}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{stats.missingInstances}</div>
              <div className="text-sm text-gray-600">Missing</div>
              <div className="text-xs text-gray-500">
                {((stats.missingInstances / stats.totalInstances) * 100).toFixed(0)}%
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Performance Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-3">
              <TrendingUp className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium">High Utilization</p>
                <p className="text-sm text-gray-600">
                  {stats.utilizationRate}% utilization is above average for this asset type.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <p className="font-medium">Maintenance Planning</p>
                <p className="text-sm text-gray-600">
                  {stats.maintenanceInstances} instances currently need attention.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cost Analysis</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Total Investment</span>
                <span className="text-lg font-bold">£{stats.totalAcquisitionCost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Maintenance Spend</span>
                <span>£{stats.totalMaintenanceCost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Per Unit Cost</span>
                <span>£{(stats.totalAcquisitionCost / stats.totalInstances).toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssetPerformanceTab;
